import { TranslationSchema } from "./de";

const en: TranslationSchema = {
    brand: {
        name: "Maison Noma",
        claim: "Objects for a better living.",
    },

    links: {
        home: "/",
        shop: "/shop",
        shopObjects: "/shop/objects",
        shopLighting: "/shop/lighting",
        shopFurniture: "/shop/furniture",
        shopTextiles: "/shop/textiles",
        cart: "/warenkorb",
        blog: "/blog",
        contact: "/kontakt",
        imprint: "/impressum",
        privacy: "/datenschutz",
        terms: "/agb",
    },

    nav: {
        shop: "Shop",
        journal: "Journal",
        contact: "Contact",
        cart: "Cart",
        search: "Search",
        menu: "Menu",
    },

    home: {
        heroSection: {
            overline: "Maison Noma",
            title: "Objects for a better living.",
            subtitle: "Thoughtfully designed objects for modern living – chosen to last, not to trend.",
            cta: "Discover the collection",
        },

        categoriesSection: {
            overline: "Categories",
            title: "Curated by category",
            description: "Four categories, one shared standard: material, form and longevity.",
            categories: [
                {
                    slug: "objects",
                    title: "Objects",
                    description: "Vases, ceramics and decorative pieces for a room's quieter moments.",
                    cta: "Shop objects",
                },
                {
                    slug: "lighting",
                    title: "Lighting",
                    description: "Table, floor and pendant lighting that shapes a room through warmth.",
                    cta: "Shop lighting",
                },
                {
                    slug: "furniture",
                    title: "Furniture",
                    description: "Small furniture in solid wood and stone, built for decades, not seasons.",
                    cta: "Shop furniture",
                },
                {
                    slug: "textiles",
                    title: "Textiles",
                    description: "Linen, wool and jute that soften a space more with every use.",
                    cta: "Shop textiles",
                },
            ],
        },

        blogTeaserSection: {
            overline: "Journal",
            title: "From the Journal",
            description: "Thoughts on material, space and the art of considered living.",
            cta: "Read all articles",
            readMore: "Read more",
        },

        productHighlightsSection: {
            overline: "Selected",
            title: "Featured Objects",
            description: "A small, carefully curated selection of our favourite pieces.",
            cta: "View all objects",
        },

        editorialSection: {
            brandStory: {
                eyebrow: "The Brand",
                title: "Designed to live with you.",
                body:
                    "Maison Noma creates timeless objects that balance material, function and form. Every piece is chosen to remain part of a home for years, not a single season.",
            },
            philosophy: {
                eyebrow: "Philosophy",
                title: "Less, but better.",
                body:
                    "We believe in conscious consumption: fewer, better-chosen objects over many interchangeable ones. Quality you can see, feel, and appreciate for years.",
            },
            finalStatement: {
                eyebrow: "For Your Home",
                title: "Every object, considered.",
                body:
                    "From first sketch to finished piece, we work with materials and craft that serve a home for years, not a single season.",
            },
        },

        newsletterSection: {
            title: "Stay informed",
            description: "Sign up to receive insights on new objects, collections and the Journal.",
            placeholder: "Your email address",
            cta: "Sign up",
            disclaimer: "By signing up you accept our privacy policy. Unsubscribe anytime.",
            successMessage: "Thanks! Please confirm your signup via the link in your email.",
            errorMessage: "Something went wrong. Please try again.",
        },
    },

    shopPage: {
        hero: {
            title: "Shop",
            description: "Discover our full collection for a thoughtfully designed home.",
        },
        breadcrumbShop: "Shop",
        filters: {
            title: "Filters",
            category: "Category",
            productType: "Object type",
            priceRange: "Price",
            priceUpTo: "up to €{value}",
            reset: "Reset filters",
            apply: "Apply",
            sortLabel: "Sort by",
            sortOptions: {
                newest: "Newest",
                priceAsc: "Price ascending",
                priceDesc: "Price descending",
                nameAsc: "Name A–Z",
            },
        },
        resultsCount: "{count} objects",
        noResults: "No objects found. Try different filters.",
        addToCart: "Add to cart",
        quickView: "Quick view",
    },

    productPage: {
        breadcrumbShop: "Shop",
        addToCart: "Add to cart",
        addedToCart: "Added to cart",
        outOfStock: "Out of stock",
        inStock: "In stock",
        lowStock: "Only a few left",
        quantityLabel: "Quantity",
        descriptionTitle: "Description",
        variantLabel: "Variant",
        relatedTitle: "You might also like",
        goToCart: "Go to cart",
        materialLabel: "Material",
        dimensionsLabel: "Dimensions",
        shippingInfoTitle: "Shipping & returns",
        shippingInfoText:
            "Ships within 3–5 business days. Free returns within 30 days. Note: this is a portfolio demo – no real shipping takes place.",
        detailsTitle: "Product details",
    },

    cartPage: {
        title: "Your cart",
        empty: "Your cart is still empty.",
        continueShopping: "Continue shopping",
        product: "Object",
        quantity: "Quantity",
        price: "Price",
        remove: "Remove",
        subtotal: "Subtotal",
        shippingNote: "Shipping costs are calculated at checkout.",
        checkoutCta: "Checkout",
        checkoutRedirectNote: "You will be redirected to a demo checkout – no real payment is processed.",
    },

    cartDrawer: {
        title: "Cart",
        empty: "Your cart is still empty.",
        subtotal: "Subtotal",
        viewCart: "View cart",
        checkout: "Checkout",
        close: "Close",
    },

    blogPage: {
        hero: {
            title: "Journal",
            description: "Thoughts on material, space and the art of considered living.",
        },
        categoryFilterLabel: "Category",
        allCategories: "All articles",
        readMore: "Read more",
        noResults: "No articles found in this category.",
    },

    blogArticlePage: {
        backToBlog: "Back to Journal",
        relatedArticlesTitle: "You might also be interested in",
        relatedProductsTitle: "Related objects",
        publishedOn: "Published on",
        minuteReadLabel: "min read",
    },

    contactPage: {
        overline: "Contact",
        title: "We're here for you",
        description:
            "Questions about an object, your order, or just feedback? Write to us – we'll get back to you as soon as possible.",
        address: "Maison Noma, Musterstraße 1, 10115 Berlin, Germany",
        phoneNumber: "+49 30 1234 5678",
        email: "hallo@maison-noma.de",
        form: {
            name: {
                label: "Name *",
                placeholder: "Your name",
                helperText: "Name must not be empty",
            },
            email: {
                label: "Email *",
                placeholder: "your@email.com",
                helperText: "Email address must not be empty",
            },
            message: {
                label: "Message *",
                placeholder: "How can we help you?",
                helperText: "Your message must contain at least 10 characters.",
            },
            terms: "I accept the ",
            termsLink: "privacy policy",
            submit: "Send message",
            successMessage: "Thanks for your message! We'll get back to you soon.",
            errorMessage: "Something went wrong. Please try again.",
        },
    },

    footerText: {
        description: "Timeless objects for modern living – thoughtfully chosen for a considered home.",
        shopTitle: "Shop",
        shopObjects: "Objects",
        shopLighting: "Lighting",
        shopFurniture: "Furniture",
        shopTextiles: "Textiles",
        allProducts: "All objects",
        contentTitle: "Journal",
        blog: "Journal",
        aboutUs: "About us",
        legalTitle: "Legal",
        imprint: "Imprint",
        privacy: "Privacy",
        terms: "Terms",
        contact: "Contact",
        copyright: "Maison Noma. All rights reserved.",
    },

    imprintText: {
        title: "Imprint",
        companyName: "Maison Noma",
        address: "Musterstraße 1, 10115 Berlin, Germany",
        representative: "Represented by: [Managing director name]",
        registerNote: "Register court: [District court] – Register number: [HRB xxxxx]",
        vatId: "VAT ID according to §27a UStG: [DE xxxxxxxxx]",
        contactTitle: "Contact",
        phone: "+49 30 1234 5678",
        email: "hallo@maison-noma.de",
        responsibleTitle: "Responsible for content according to § 55 Abs. 2 RStV",
        responsibleName: "[Name of the responsible person]",
        disclaimerTitle: "Notice",
        disclaimerText:
            "This is a placeholder page for a portfolio project. Maison Noma is a fictional brand; no real sales take place on this website. Final legal details would need to be reviewed by legal counsel before any real launch.",
    },

    privacyText: {
        title: "Privacy Policy",
        responsibleTitle: "Data controller",
        responsibleText: "Maison Noma, Musterstraße 1, 10115 Berlin, hallo@maison-noma.de",
        purposeTitle: "Purpose of this demo",
        purposeText:
            "This website is a pure portfolio demonstration. No real orders, payments or customer data are processed; the checkout process is simulated.",
        collectedDataTitle: "Data collected",
        collectedDataText:
            "This demo uses local browser storage (localStorage) solely to simulate the shopping cart. No data is transmitted to a server.",
        thirdPartyTitle: "Third-party services",
        thirdPartyText: "No external payment or shipping providers are integrated. Product images are loaded from Unsplash.",
        legalBasisTitle: "Legal basis",
        legalBasisText: "Not applicable – no personal data is processed.",
        storageDurationTitle: "Storage duration",
        storageDurationText: "Cart data remains solely in your local browser until you clear it.",
        yourRightsTitle: "Your rights",
        yourRightsText: "As no personal data is processed, the usual data-subject rights do not apply to this demo.",
        disclaimerText: "This is a placeholder page for a portfolio project and does not replace a legal review before any real launch.",
    },

    termsText: {
        title: "Terms & Conditions",
        scopeTitle: "Scope",
        scopeText: "This website is a portfolio demonstration of the fictional brand Maison Noma. No real purchase contract is formed.",
        contractTitle: "Conclusion of contract",
        contractText:
            "The checkout on this website is a simulation for demonstration purposes. No order is placed and no payment is processed.",
        pricesTitle: "Prices & shipping",
        pricesText: "All prices shown are fictional and for illustration purposes only.",
        withdrawalTitle: "Right of withdrawal",
        withdrawalText: "As no real purchase takes place, a right of withdrawal does not apply.",
        disclaimerText: "This is a placeholder page for a portfolio project and does not replace a legal review before any real launch.",
    },

    common: {
        loading: "Loading…",
        error: "Something went wrong.",
        currency: "EUR",
        from: "from",
        skipToContent: "Skip to content",
        newBadge: "New",
        wasPrice: "Was",
        youSave: "Save",
        addToWishlist: "Add to wishlist",
        removeFromWishlist: "Remove from wishlist",
        addToCart: "Add to cart",
        added: "Added",
    },

    checkoutDemoPage: {
        title: "This is a demo",
        message: "Maison Noma is a portfolio project. No real order was placed and no payment was processed.",
        backToShopCta: "Back to shop",
    },

    notFoundPage: {
        title: "Page not found",
        description: "The page you're looking for doesn't exist or has been moved.",
        cta: "Back to homepage",
    },
};

export default en;
